import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, auth } from '../api/client';
import { useToast } from '../components/Toast';
import { answerSheetsZipName, downloadAnswerSheet, downloadAnswerSheetsZip } from '../lib/answerSheet';
import type { AttemptReviewSummary, AuthorExam, ReviewBundle } from '../types/models';

/** Fetch review bundles for many attempts with a small concurrency cap (keeps a class download
 *  responsive without hammering the API). Reports progress as each one lands. */
async function fetchBundles(ids: string[], onProgress: (done: number) => void): Promise<ReviewBundle[]> {
  const out: ReviewBundle[] = new Array(ids.length);
  let next = 0, done = 0;
  const workers = Array.from({ length: Math.min(5, ids.length) }, async () => {
    for (let i = next++; i < ids.length; i = next++) {
      out[i] = await api.getReviewBundle(ids[i]);
      onProgress(++done);
    }
  });
  await Promise.all(workers);
  return out;
}

export function AttemptListPage() {
  const { examId, orgId } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const [attempts, setAttempts] = useState<AttemptReviewSummary[] | null>(null);
  const [exam, setExam] = useState<AuthorExam | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const user = auth.getUser();

  // Return to wherever "Review submissions" was launched from: the org-scoped exam
  // list (admin, with sidebar) or the standalone exams page (faculty).
  const examsPath = orgId ? `/org/${orgId}/exams` : '/exams';
  const attemptPath = (attemptId: string) =>
    orgId ? `/org/${orgId}/exams/${examId}/review/${attemptId}` : `/exam/${examId}/review/${attemptId}`;

  useEffect(() => {
    api.listExamAttempts(examId!).then(setAttempts).catch(e => setError(e.message));
    api.getAuthorExam(examId!).then(setExam).catch(() => { /* title is best-effort */ });
  }, [examId]);

  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allIds = (attempts ?? []).map(a => a.attemptId);
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));

  // Download one attempt's answer sheet (RegNo_ExamName_date.pdf).
  async function downloadOne(attemptId: string) {
    if (busy) return;
    setBusy(true);
    try {
      downloadAnswerSheet(await api.getReviewBundle(attemptId));
      toast.success('Answer sheet downloaded.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to download answer sheet.');
    } finally {
      setBusy(false);
    }
  }

  // Download many answer sheets (selected, or all when none are ticked) as a single ZIP.
  async function downloadMany() {
    if (busy) return;
    const ids = selected.size > 0 ? allIds.filter(id => selected.has(id)) : allIds;
    if (ids.length === 0) return;
    setBusy(true);
    setProgress({ done: 0, total: ids.length });
    try {
      const bundles = await fetchBundles(ids, done => setProgress({ done, total: ids.length }));
      await downloadAnswerSheetsZip(bundles, answerSheetsZipName(exam?.title ?? 'Exam'));
      toast.success(`Downloaded ${bundles.length} answer sheet${bundles.length === 1 ? '' : 's'}.`);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to download answer sheets.');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  const hasAttempts = !!attempts && attempts.length > 0;
  const manyLabel = busy && progress
    ? `Preparing… ${progress.done}/${progress.total}`
    : selected.size > 0
      ? `⬇ Download selected (${selected.size})`
      : `⬇ Download all answer sheets${hasAttempts ? ` (${allIds.length})` : ''}`;

  return (
    <>
      <header className="topbar">
        <div className="brand">Exam App · Review</div>
        <div className="row" style={{ gap: 12 }}>
          <span style={{ fontSize: 13 }}>{user?.fullName} · {user?.role}</span>
          <button onClick={() => nav(examsPath)} className="topbar-ghost">
            Back to exams
          </button>
        </div>
      </header>

      <div className="container">
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <h2 style={{ marginBottom: 4 }}>Submissions</h2>
              {exam && (
                <div className="muted" style={{ marginBottom: 12 }}>
                  {exam.title}
                  {exam.batchCode ? ` · ${exam.batchCode} — ${exam.batchName}` : ''}
                </div>
              )}
            </div>
            {hasAttempts && (
              <button className="primary" onClick={downloadMany} disabled={busy}
                      title="Download answer sheets (answers only) as a ZIP of PDFs">
                {manyLabel}
              </button>
            )}
          </div>
          {error && <div className="error">{error}</div>}
          {!attempts && !error && <div className="muted">Loading…</div>}
          {attempts && attempts.length === 0 && (
            <div className="muted">No students have started this exam yet.</div>
          )}
          {hasAttempts && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '8px 0', width: 28 }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                           aria-label="Select all submissions" style={{ width: 'auto' }} />
                  </th>
                  <th>Student</th>
                  <th>Reg No</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Score</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {attempts.map(a => (
                  <tr key={a.attemptId} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 0' }}>
                      <input type="checkbox" checked={selected.has(a.attemptId)}
                             onChange={() => toggle(a.attemptId)} style={{ width: 'auto' }}
                             aria-label={`Select ${a.studentName}`} />
                    </td>
                    <td>{a.studentName}</td>
                    <td>{a.rollNumber ?? '—'}</td>
                    <td>
                      <StatusBadge status={a.status} />
                      {a.reevaluationStatus === 'Requested' && (
                        <span style={{
                          display: 'inline-block', marginLeft: 6, padding: '2px 8px', borderRadius: 999,
                          fontSize: 11, background: 'var(--warn-soft-bg)', color: 'var(--warn-soft-fg)'
                        }}>Re-eval requested</span>
                      )}
                    </td>
                    <td>{a.submittedAt ? new Date(a.submittedAt).toLocaleString() : '—'}</td>
                    <td>{a.scoreAwarded ?? '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => downloadOne(a.attemptId)} disabled={busy}
                              title="Download this student's answer sheet (PDF)"
                              style={{ marginRight: 8 }}>
                        ⬇ Answer sheet
                      </button>
                      <Link to={attemptPath(a.attemptId)}>
                        <button className="primary">Open</button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const style: React.CSSProperties = {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: 12,
    background: 'var(--bg)',
    color: 'var(--muted)'
  };
  if (status === 'Submitted' || status === 'AutoSubmitted') {
    style.background = 'var(--success-soft-bg)'; style.color = 'var(--success-soft-fg)';
  } else if (status === 'InProgress') {
    style.background = 'var(--warn-soft-bg)'; style.color = 'var(--warn-soft-fg)';
  } else if (status === 'Disqualified') {
    style.background = 'var(--danger-soft-bg)'; style.color = 'var(--danger-soft-fg)';
  }
  return <span style={style}>{status}</span>;
}
